import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";

import { showError } from "redux/actions/ui";
import { ILocation, IModule } from "redux/reducers/models/reservations";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import DataTable from "../../../../../components/dataTable/DataTable";
import {
  GetAllReservationLocation,
  GetReservationModuleAll,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";

export default function Modules() {
  const history = useHistory();
  const { t } = useTranslation();
  const { authStore } = useAppSelector(store => store);

  const dispatch = useAppDispatch();
  const [modules, setModules] = useState<Array<IModule & { locationCount: number }>>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAllReservationModules(source.token);
    return () => source.cancel();
  }, []);

  async function loadAllReservationModules(token?: CancelToken) {
    if (modules !== undefined) {
      setModules(undefined);
    }
    const [moduleRes, locationRes] = await Promise.all([
      GetReservationModuleAll(null, token ? false : true, token),
      GetAllReservationLocation(token ? false : true, token),
    ]);

    if (token && token.reason) {
      return;
    }
    if (moduleRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading activity types")));
      setModules([]);
      return;
    }

    if (locationRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading units")));
      setModules([]);
      return;
    }

    const locations: ILocation[] = Array.isArray(locationRes.data) ? (locationRes.data as ILocation[]) : [];

    const modulesWithLocations = moduleRes?.data?.reduce((updatedModules, currentModule) => {
      const locationCount = locations.filter(location => location?.reservation_module_id === currentModule?.id).length;
      return [...updatedModules, { ...currentModule, locationCount }];
    }, [] as Array<IModule & { locationCount: number }>);
    setModules(modulesWithLocations);
  }

  return (
    <Page
      title={t("Activity Types")}
      subtitle={t("Manage activity types")}
      narrow
      primaryAction={{
        content: t("New Activity Type"),
        action: () => history.push("/admin/settings/reservations/module/new"),
        disabled: !authStore?.user?.permissions?.reservations_module_new,
      }}
    >
      <DataTable
        columns={[{ label: t("Title") }, { label: t("Locations") }]}
        loading={modules === undefined}
      >
        {modules?.map(module => (
          <tr
            key={module.id}
            onClick={() => history.push("/admin/settings/reservations/module/" + String(module.id))}
            className="clickable"
          >
            <td>{module.title}</td>
            <td>{module.locationCount}</td>
          </tr>
        ))}
      </DataTable>
    </Page>
  );
}
